package com.iregained.site.components.sections.home.sections

import androidx.compose.runtime.*
import com.iregained.site.components.sections.home.styles.AudienceSelectionStyle
import com.iregained.site.components.sections.product.styles.KnowMoreLinkStyle
import com.iregained.site.components.widgets.SectionHeader
import com.iregained.site.theme.SubheadlineTextStyle
import com.iregained.site.theme.toSitePalette
import com.iregained.site.theme.unextractable
import com.iregained.site.utils.Res
import com.varabyte.kobweb.browser.dom.observers.IntersectionObserver
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.css.JustifyContent
import com.varabyte.kobweb.compose.css.Transition
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.graphics.Image
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div

@Composable
private fun Modifier.slideInFromSide(isVisible: Boolean, isLeft: Boolean, delayMs: Int): Modifier {
    val startX = if (isLeft) (-100).px else 100.px
    return this.opacity(if (isVisible) 1 else 0).translateX(if (isVisible) 0.px else startX).transition(
        Transition.of(
            "opacity", duration = 0.8.s, timingFunction = TransitionTimingFunction.EaseOut, delay = delayMs.ms
        ), Transition.of(
            "transform", duration = 0.8.s, timingFunction = TransitionTimingFunction.EaseOut, delay = delayMs.ms
        )
    )
}

@Composable
fun HomeAudienceCardsSection(data: HomeAudienceCardsSectionData = defaultHomeAudienceCardsSectionData) {
    val palette = ColorMode.current.toSitePalette()
    val breakpoint = com.iregained.site.utils.LocalBreakpoint.current
    val isDesktop = breakpoint >= Res.Breakpoints.LG
    val targetId = data.sectionId?.takeIf { it.isNotBlank() } ?: "audience-paths"
    var isVisible by remember { mutableStateOf(false) }

    if (data.cards.isNotEmpty()) {
        Column(
            modifier = Modifier.id(targetId).fillMaxWidth().backgroundColor(palette.brand.primarySoft)
                .padding(topBottom = if (isDesktop) 50.px else 25.px, leftRight = 5.percent)
                .styleModifier { property("scroll-margin-top", "100px") },
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Div(attrs = Modifier.fillMaxWidth().toAttrs {
                ref { element ->
                    val observer = IntersectionObserver { entries, obs ->
                        entries.forEach {
                            if (it.isIntersecting) {
                                isVisible = true; obs.unobserve(element)
                            }
                        }
                    }
                    observer.observe(element)
                    onDispose { observer.disconnect() }
                }
            }) {
                Column(horizontalAlignment = Alignment.CenterHorizontally) {
                    if (data.sectionHeader.isNotBlank()) {
                        SectionHeader(
                            text = data.sectionHeader,
                            fontSize = 32.px,
                            lineWidthPercent = 25.percent,
                            modifier = Modifier.margin(bottom = 16.px).opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s)),
                            alignment = Alignment.CenterHorizontally
                        )
                    }

                    if (data.sectionSubtext.isNotBlank()) {
                        Div(
                            attrs = SubheadlineTextStyle.toModifier().maxWidth(1440.px).fontSize(18.px)
                                .fontWeight(FontWeight.Medium).color(palette.custom.darkText).margin(bottom = 40.px)
                                .opacity(if (isVisible) 1 else 0)
                                .transition(Transition.of("opacity", 0.8.s, delay = 200.ms)).toAttrs {
                                    ref { htmlElement -> htmlElement.innerHTML = data.sectionSubtext; onDispose {} }
                                })
                    }

                    Row(
                        modifier = Modifier.fillMaxWidth().maxWidth(1440.px).gap(50.px).display(DisplayStyle.Flex)
                            .flexWrap(FlexWrap.Wrap).justifyContent(JustifyContent.Center)
                    ) {
                        data.cards.forEachIndexed { index, audience ->
                            key(audience.title) {
                                AudienceSelectionCard(
                                    // ANIMATION: Evens slide from left, odds slide from right
                                    modifier = Modifier.flex(1).minWidth(300.px).maxWidth(600.px)
                                        .slideInFromSide(isVisible, isLeft = index % 2 == 0, delayMs = 400),
                                    data = audience
                                )
                            }
                        }
                    }
                }
            }
        }
    }
}

@Composable
private fun AudienceSelectionCard(modifier: Modifier, data: AudienceCardData) {
    val palette = ColorMode.current.toSitePalette()
    A(
        href = data.linkPath,
        attrs = AudienceSelectionStyle.toModifier().then(modifier).backgroundColor(Colors.White).borderRadius(16.px)
            .overflow(Overflow.Hidden).textDecorationLine(TextDecorationLine.None).toAttrs()
    ) {
        Column(modifier = Modifier.fillMaxWidth()) {
            if (data.imagePath.isNotBlank()) Image(
                src = data.imagePath,
                modifier = Modifier.unextractable().fillMaxWidth().height(250.px).objectFit(ObjectFit.Cover)
            )
            Column(modifier = Modifier.fillMaxWidth().padding(30.px)) {
                if (data.title.isNotBlank()) {
                    Div(
                        attrs = Modifier.fontSize(24.px).fontWeight(FontWeight.Bold).color(palette.custom.darkText)
                            .opacity(0.75f).margin(bottom = 12.px).toAttrs {
                                ref { htmlElement -> htmlElement.innerHTML = data.title; onDispose {} }
                            })
                }

                Div(
                    attrs = Modifier.fontSize(16.px).lineHeight(1.6).color(palette.custom.darkText)
                    .margin(bottom = 24.px).styleModifier {
                        property("display", "-webkit-box"); property(
                        "-webkit-line-clamp", "3"
                    ); property("-webkit-box-orient", "vertical"); property("overflow", "hidden")
                    }.toAttrs { ref { htmlElement -> htmlElement.innerHTML = data.description; onDispose {} } })
                if (data.buttonText.isNotBlank()) SpanText(
                    text = "${data.buttonText} →",
                    modifier = KnowMoreLinkStyle.toModifier().fontSize(18.px).fontWeight(FontWeight.Bold)
                        .color(palette.brand.primary)
                )
            }
        }
    }
}

/**
 * CMS IMAGE UPLOAD SPECS FOR THIS SECTION:
 * @field imagePath (inside AudienceCardData):
 * - Aspect Ratio: Flexible, but 16:9 (Landscape) recommended to fit card width.
 * - Recommended Dimensions: 600x350px.
 * - Max File Size: 250KB (WebP or JPEG).
 * - Behavior: Enforced height of 250px via code; ObjectFit.Cover will crop overflow to maintain perfect card symmetry.
 */
data class AudienceCardData(
    val title: String, val description: String, val imagePath: String, val linkPath: String, val buttonText: String
)

data class HomeAudienceCardsSectionData(
    val sectionId: String? = null,
    val sectionHeader: String,
    val sectionSubtext: String,
    val cards: List<AudienceCardData>
)

val defaultHomeAudienceCardsSectionData = HomeAudienceCardsSectionData(
    sectionId = "home-audience-paths",
    sectionHeader = "CHOOSE YOUR PATHWAY",
    sectionSubtext = "Tailored experiences for those seeking recovery and those providing care.",
    cards = listOf(
        AudienceCardData(
            "For Patients & Caregivers",
            "Reclaim hand function and independence from the comfort of your home with the MyHand<sup>®</sup> System.",
            Res.Images.Home.Audience.PATIENT_PATH,
            "/patients",
            "Explore Patient Journey"
        ), AudienceCardData(
            "For Clinicians & Facilities",
            "Enhance neuro-rehab outcomes, track patient data in the cloud, and increase clinical throughput.",
            Res.Images.Home.Audience.CLINICIAN_PATH,
            "/clinicians",
            "Explore Clinical Outcomes"
        )
    )
)